import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDateTime, fmtDate } from "@/lib/helpers";
import { History as HistoryIcon, CalendarClock, CheckCircle2, Clock } from "lucide-react";

const RESULT_COLOR = {
  pmt_ok: "bg-green-500", pmt_non_signee: "bg-orange-500", pmt_non_realisee: "bg-red-500",
  refus: "bg-red-500", a_demander: "bg-accent-500", revenir_plus_tard: "bg-orange-500",
  autre: "bg-accent-500", cloture_manuel: "bg-green-500",
};

// Vérification compacte des RDV transmis par le chauffeur : En attente de prise en compte / Pris en compte le … (la notification reste l'info principale).
function TransmittedAppointments() {
  const [rows, setRows] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => { api.get("/appointments/mine-transmitted").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows || rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, 5);
  return (
    <section data-testid="transmitted-appointments" className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-3">
      <h2 className="text-base font-heading font-bold text-brand-900 flex items-center gap-2 mb-1"><CalendarClock className="w-4 h-4" />Mes rendez-vous transmis <span className="text-xs font-normal text-slate-400">({rows.length})</span></h2>
      <ul className="divide-y divide-slate-100">
        {shown.map((a) => (
          <li key={a.id} data-testid={`transmitted-${a.id}`} className="py-2 text-sm">
            <p className="font-semibold text-slate-800 truncate">{fmtDate(a.date)}{a.time ? ` · ${a.time}` : ""} <span className="text-slate-500 font-normal">· {a.type_label}{a.series ? ` ${a.series}` : ""}</span>{a.place && <span className="text-slate-400 font-normal"> · {a.place}</span>}</p>
            <p data-testid={`transmitted-status-${a.id}`} className={`text-[11px] font-bold inline-flex items-center gap-1 ${a.confirmed ? "text-green-700" : "text-orange-700"}`}>{a.confirmed ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Clock className="w-3.5 h-3.5" />}{a.confirmed_label}</p>
          </li>
        ))}
      </ul>
      {rows.length > 5 && <button type="button" data-testid="transmitted-toggle" onClick={() => setAll(!all)} className="w-full h-9 text-xs font-bold text-accent-700">{all ? "Réduire" : `Voir les ${rows.length - 5} autres`}</button>}
    </section>
  );
}

export default function DriverHistory() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get("/history/recent").then((r) => setItems(r.data)).catch(() => setItems([])); }, []);

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-heading font-extrabold text-brand-900">Historique récent</h1>
      <TransmittedAppointments />
      {items === null && <p className="text-slate-400 py-10 text-center">Chargement…</p>}
      {items && items.length === 0 && (
        <div className="text-center py-16 text-slate-400"><HistoryIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p className="font-semibold">Aucune tentative enregistrée.</p></div>
      )}
      <div className="space-y-2">
        {items?.map((h) => (
          <div key={h.id} data-testid={`history-${h.id}`} className="bg-white rounded-2xl border border-slate-200/70 p-4 shadow-sm flex gap-3">
            <div className={`w-2.5 h-2.5 rounded-full mt-2 shrink-0 ${RESULT_COLOR[h.result] || "bg-slate-400"}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <p className="font-heading font-bold text-brand-900">{h.result_label}</p>
                <span className="text-xs text-slate-400 shrink-0">{fmtDateTime(h.created_at)}</span>
              </div>
              <p className="text-sm text-slate-500">{h.patient_name} · {h.establishment_name}</p>
              <p className="text-sm text-slate-400">Par {h.driver_name}</p>
              {h.comment && <p className="text-sm text-slate-600 mt-1 italic">« {h.comment} »</p>}
              {h.next_date && <p className="text-sm text-orange-600 mt-0.5">Revenir le {fmtDate(h.next_date)}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
